import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";

import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function IslamicTradePage8() {
  const contentRef = useRef(null);
  const links = {
    prev: "/islamic-trade-page-7",
    index: "/index",
    next: "/islamic-trade-page-9",
  };
  const titleText = "Sale of Land and Attached Assets (Bay al-Usool wa Thimar)";
  const questions = [
    {
      text: "1.  According to Islamic jurisprudence, what happens to trees planted on land when the land is sold?",
      options: [
        {
          value: "A",
          label: "A. They must be removed before the sale.",
        },
        {
          value: "B",
          label: "B. They are sold separately from the land.",
        },
        {
          value: "C",
          label: "C. Their ownership transfers to the buyer.",
        },
        {
          value: "D",
          label: "D. They remain the property of the community.",
        },
      ],
      correct: "C",
    },
    {
      text: "2. What is the Islamic ruling about herbs, grass, and pasture that grow naturally on a piece of land?",
      options: [
        {
          value: "A",
          label: "A. They are the exclusive property of the landowner",
        },
        { value: "B", label: "B. They are taxed before use" },
        { value: "C", label: "C. They are considered communal resources" },
        { value: "D", label: "D. They must be harvested and sold immediately" },
      ],
      correct: "C",
    },
    {
      text: "3. If a crop like wheat is already growing on the land at the time of sale, who owns it?",
      options: [
        { value: "A", label: "A. The buyer, by default" },
        { value: "B", label: "B. The government" },
        { value: "C", label: "C. The seller, unless stipulated otherwise" },
        { value: "D", label: "D. The local community" },
      ],
      correct: "C",
    },
    {
      text: "4. What does the Hadith about pollinated date palms primarily teach us?",
      options: [
        {
          value: "A",
          label: "A. Fruits are always shared equally between buyer and seller",
        },
        { value: "B", label: "B. Buyers must pollinate the trees themselves" },
        {
          value: "C",
          label:
            "C. Pollinated fruits belong to the seller unless the buyer stipulates otherwise",
        },
        {
          value: "D",
          label: "D. Trees and fruits must never be sold together",
        },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={8}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 8</b> of our learning journey. Today, we will explore
        the Islamic rulings on land ownership and the rights associated with its
        produce. Through the lens of the Qur'an, Sunnah, and scholarly
        interpretation, we will examine how Islam governs the sale of immovable
        assets, trees, crops, and naturally growing resources like grass and
        herbs. We will also uncover the wisdom behind these rulings, how they
        protect prior effort, promote transparency, and preserve communal
        welfare. As always, the goal is to deepen our understanding of how
        Islamic law provides clear and just solutions in matters of ownership
        and trade. Let us begin with the intention to seek knowledge that
        benefits ourselves and our communities.
        <br />
        <br /> Islamic Law offers comprehensive guidance on the ownership of
        land, its assets, and its produce, ensuring justice and clarity in
        property transactions. Below, we explore various aspects of land
        ownership in light of the Qur'an, Sunnah, and scholarly interpretations.
        <br />
        <br />
        <b>Immovable Assets Included in the Sale</b>
        <br />
        <br />
        When a person buys land or real estate, the purchase includes the
        ownership of all immovable assets associated with the property. This
        includes walls, roofs, stairs, doors, foundational stones, and fences.
        These elements are considered part of the physical essence of the
        property and inseparable from it in ownership. The ownership of these
        immovable structures is implicit in the transaction, as they are
        integral components that define the property’s identity.
        <br />
        In Islamic jurisprudence, the concept of immovable property (known as
        “aqār”) carries particular significance because it establishes a direct
        connection between ownership and the earth. Ownership of such property
        is not merely about possessing physical land, but also the structures
        that give the property function and value. When one purchases a piece of
        land with a house, the house’s walls, foundation, and roof are naturally
        included because they cannot be separated from the land without
        destroying its integrity.
        <br />
        This principle aligns with the broader Islamic emphasis on clarity and
        fairness in trade, ensuring that no ambiguity exists about what is
        included in the sale. The Prophet Muhammad (peace be upon him) forbade
        transactions that contained uncertainty (gharar), and scholars have
        consistently applied this rule to property sales. The buyer must know
        exactly what he is purchasing, and the seller must not conceal or
        obscure any detail that could later lead to dispute.
        <br />
        For example, if someone sells a home and does not clarify whether
        attached structures such as a water tank, courtyard pavement, or
        built-in shelves are included in the sale, disputes can arise later.
        Islamic jurists address such cases by applying the principle of what is
        fixed or inseparable from the land (taabi‘ lil ‘ayn). Therefore, all
        immovable parts that cannot be removed without damage are considered
        included by default.
        <br />
        In the classical fiqh texts, jurists like Imam Abu Hanifa, Imam Malik,
        and Imam al-Shafi‘i discussed in detail the difference between immovable
        and movable property in sales. According to the Hanafi school, any
        structure that is permanently attached to the land, such as a well, a
        wall, or a staircase, automatically transfers to the buyer. The Shafi‘i
        school adds that even if the contract does not specify these items, the
        law assumes inclusion because the buyer’s intent in purchasing land
        naturally encompasses the structures that make it functional.
        <br />
        In practical terms, this rule provides legal clarity and economic
        justice. It protects the buyer’s rights, ensuring he receives what he
        reasonably expects when purchasing property. It also safeguards the
        seller by preventing the buyer from later claiming rights over items
        that were not part of the transaction. In essence, Islamic law protects
        both sides from deceit or misunderstanding, which is central to the
        Shariah’s aim of preventing conflict and ensuring mutual satisfaction in
        all dealings.
        <br />
        From a modern perspective, this principle remains deeply relevant. Real
        estate contracts today still hinge on clear identification of included
        structures. Whether it is a modern apartment, a piece of agricultural
        land, or a commercial building, the buyer and seller must know exactly
        what is part of the deal. By establishing this rule centuries ago,
        Islamic law laid the foundation for ethical real estate practices that
        prevent ambiguity and uphold justice.
        <br />
        <br />
        <b>Ownership of Trees on the Land</b>
        <br />
        <br />
        Trees planted on the land are included in the sale, and their ownership
        transfers to the buyer. This is because planted trees are considered
        part of the property.
        <br /> The Prophet Muhammad (peace be upon him) said:
        <br /> "If somebody buys date-palms after they have been pollinated, the
        fruits will belong to the seller unless the buyer stipulates the
        contrary. If somebody buys a slave having some property, the property
        will belong to the seller unless the buyer stipulate that it should
        belong to him."
        <a href="https://sunnah.com/bukhari:2379" target="blank">
          (Sahih Al-Bukhari 2379)
        </a>
        <br />
        <br />
        This Hadith indicates that ownership of produce can be explicitly
        stipulated, but without such a stipulation, ownership of the trees and
        their future yield generally passes to the buyer.
        <br />
        Trees are viewed in Islamic jurisprudence as fixed and permanent
        features of the land, much like the land’s foundation itself. When the
        trees are rooted and nurtured by the land, their ownership follows the
        ownership of that land. However, when the trees are at a stage of
        producing fruit, a distinction arises between the tree itself and its
        immediate produce. The Prophet’s Hadith clarifies this delicate point by
        differentiating between pollinated and unpollinated date palms.
        Pollination represents human effort; thus, the yield belongs to the one
        who made that effort unless agreed otherwise. The jurists of the four
        schools further analyzed this Hadith to ensure its fair application. The
        Maliki jurists emphasized that the buyer should receive everything that
        is considered a permanent feature of the land, but not temporary yields
        unless specified. The Hanbali scholars added that the principle of
        fairness demands that if the buyer is unaware of the state of
        pollination at the time of purchase, he should have the option to adjust
        the contract or cancel it to avoid unfairness. This reveals the high
        level of ethical care embedded in Islamic commercial law.
        <br />
        This ruling also prevents deceit or unjust enrichment. Suppose a seller
        sells an orchard right after pollination, fully aware that the fruits
        will ripen soon, without disclosing that fact to the buyer. If no
        stipulation is made, Islamic law protects the buyer by allowing him to
        renegotiate or cancel. On the other hand, if both parties agree that the
        fruit belongs to the seller, then both must honor this commitment. Thus,
        justice and mutual consent are the foundation.
        <br />
        The application of this rule extends beyond ancient orchards. In modern
        agriculture, where land sales may involve plantations, vineyards, or
        fruit farms, these principles remain practical. If a company buys
        farmland with mature trees, the default assumption is that the trees
        belong to the buyer. However, the fruit or crops already close to
        harvest may belong to the seller unless explicitly transferred. This
        encourages clear contractual agreements, preventing disputes in modern
        contexts just as it did in the Prophet’s time.
        <br />
        Moreover, this rule embodies a moral dimension. It honors human labor
        and recognizes the effort invested in cultivation. Islam teaches that
        reward follows effort, and thus the one who pollinated, watered, and
        nurtured the trees deserves the fruit of that work. The buyer, in turn,
        benefits from future yields. This balance between effort and ownership
        is one of the hallmarks of the Shariah’s economic system.
        <br />
        <br />
        <b>Herbs, Grass, and Pasture Grown Organically</b>
        <br />
        <br />
        Herbs, grass, and pasture that grow organically on a piece of land
        without human intervention are considered communal resources in Islamic
        jurisprudence. <br />
        This principle stems from the Hadith of the Prophet Muhammad (peace be
        upon him):
        <br /> "Muslims have common share in three (things) grass, water and
        fire."
        <i>
          <a href="https://sunnah.com/abudawud:3477" target="blank">
            (Sunan Abu Dawood 1307)
          </a>
        </i>
        <br />
        The wisdom behind this ruling lies in the Islamic emphasis on preserving
        communal resources and ensuring equitable access for all members of
        society. Herbs and grass that grow naturally are not a product of human
        labor or investment, and thus, they remain outside the realm of
        exclusive ownership.
        <br />
        Islamic jurists, when discussing this Hadith, recognized it as a
        cornerstone for environmental justice and community welfare. Natural
        grasslands, flowing water, and firewood are gifts of Allah to all
        people, not the product of individual effort. Therefore, no one should
        claim exclusive control over them unless he has invested labor or
        capital to develop or manage them. This ruling ensures that basic
        natural resources remain available to all, especially the poor and
        travelers.
        <br />
        However, if the herbs or grass are cultivated by the landowner, they
        become part of the property and belong to the buyer upon sale. Naturally
        grown pasture, however, remains accessible to the community, ensuring
        fairness and sustainability. This principle also aligns with broader
        Islamic teachings that encourage the preservation of natural resources
        and discourage wastefulness or hoarding.
        <br />
        From a social perspective, this rule promotes cooperation and reduces
        conflict over land use. In traditional societies, disputes often arose
        over grazing rights and water access. By declaring these natural
        resources as shared, Islam prevented monopolization by the wealthy and
        safeguarded the livelihood of herders and common people. This reflects
        the Qur'anic vision of balance in the natural world and equitable
        distribution of resources.
        <br />
        In contemporary terms, this principle can guide policies on public land,
        forests, and environmental management. For instance, the idea that
        naturally growing grass and herbs belong to everyone can translate today
        into laws ensuring public access to parks, rivers, and natural reserves.
        Islam thus provides a timeless foundation for ecological ethics and
        sustainability.
        <br />
        Furthermore, this ruling encourages gratitude and humility. Since
        natural resources are not a result of personal effort, they remind
        humans of their dependence on Allah’s mercy. It discourages arrogance
        and greed, fostering a spirit of sharing and collective welfare.
        <br />
        <br />
        <b>Crops on the Land at the Time of Sale</b>
        <br />
        <br />
        Ownership of crops growing on the land at the time of sale depends on
        their nature.
        <br />
        One-Time Crops such as wheat or barley are temporary and represent the
        result of the seller’s effort. Therefore, the seller retains ownership
        of these crops until they ripen, as they represent his prior labor and
        investment. This principle is supported by the Hadith regarding
        pollinated date palms, emphasizing that existing fruits or crops are
        owned by the seller unless otherwise stipulated.
        <br />
        Recurring Crops such as clover behave differently. The crop on the tip
        (the current yield) belongs to the seller at the time of sale, but the
        roots and stems become the buyer’s property. Any future growth from the
        same plant belongs to the buyer. This distinction ensures fairness by
        acknowledging the seller’s work while granting the buyer rights to
        future yields.
        <br />
        This framework shows Islam’s precision in addressing the complexities of
        ownership. It recognizes the dynamic nature of agriculture, where both
        human effort and natural processes are involved. The seller is rewarded
        for his existing effort, and the buyer gains ownership of what grows
        afterward. Thus, both benefit in proportion to their involvement.
        <br />
        In practical agricultural transactions, this principle provides a fair
        division of benefit. Imagine a farmer selling land that already contains
        a standing wheat crop. The buyer purchases the land but not the wheat
        unless explicitly agreed. The wheat remains the seller’s property
        because it reflects his labor and expense. Once harvested, however, the
        land and its potential for new crops belong to the buyer.
        <br />
        Jurists have extended this principle to modern forms of farming. For
        instance, when a farm is sold with standing sugarcane or rice, the
        default rule is that the crops belong to the seller unless stipulated
        otherwise. But if the land contains perennial plants that yield multiple
        times per year, such as banana trees or clover, the seller keeps only
        the current harvest while the buyer inherits the source for future
        yields.
        <br />
        This nuanced distinction ensures that no party is wronged. The Shariah
        avoids injustice (zulm) in all its forms, ensuring that effort and
        benefit remain aligned. It prevents the exploitation of either party,
        guaranteeing that trade serves as a means of cooperation, not conflict.
        <br />
        <br />
        <b>Ownership rights over pollinated fruits</b>
        <br />
        <br />
        When someone sells a pollinated tree, such as a date palm, the fruits
        that have already been pollinated belong to the seller unless the buyer
        explicitly stipulates that the fruits will belong to him. This
        principle, derived from the Hadith, "If somebody buys date-palms after
        they have been pollinated, the fruits will belong to the seller unless
        the buyer stipulates the contrary. If somebody buys a slave having some
        property, the property will belong to the seller unless the buyer
        stipulate that it should belong to him."
        <i>
          <a href="https://sunnah.com/bukhari:2379" target="blank">
            (Sahih Al-Bukhari 2379)
          </a>
        </i>{" "}
        , also applies to other fruit-bearing trees such as berries, grapes,
        pomegranates, and similar produce. This ruling underscores the
        importance of clarifying the terms of a sale in Islamic transactions.
        The pollinated fruits represent a result of the seller’s effort and
        resources, which are not inherently included in the sale of the tree
        unless explicitly agreed upon. Islam emphasizes justice and fairness in
        trade, ensuring that neither party is wronged or left with ambiguous
        expectations.
        <br />
        <br />
        <b>Wisdom Behind These Rulings</b>
        <br />
        <br />
        Acknowledging Effort and Investment:
        <br />
        The seller’s labor and resources used to cultivate the land or crops are
        recognized and rewarded. Islam never allows one person to profit
        unjustly from another’s effort. This is why standing crops or pollinated
        fruit belong to the one who worked for them. It embodies the principle
        that “man can have nothing but what he strives for.” Every act of
        cultivation, watering, or care is thus given its rightful recognition.
        <br />
        Promoting Transparency:
        <br />
        Clear guidelines ensure that both parties understand their rights,
        reducing disputes. The Shariah discourages vague agreements that might
        cause conflict. By setting explicit rules about what is included in a
        land sale, Islam ensures transparency and accountability in all
        dealings.
        <br />
        Preservation of Shared Resources:
        <br />
        The ruling on grass, herbs, and pasture reinforces the communal nature
        of certain resources, preventing exploitation. This ensures that wealth
        and resources circulate in society rather than being hoarded by a few.
        It reflects the broader Islamic economic philosophy, which discourages
        monopolies and encourages social responsibility.
        <br />
        Economic Justice:
        <br />
        Buyers and sellers receive equitable treatment, balancing immediate and
        future benefits. The seller is compensated for his work, while the buyer
        gains ownership of the land’s enduring potential. This balance ensures
        social harmony and economic stability, key objectives of Islamic law.
        <br />
        Islamic scholars have elaborated on these rulings to provide practical
        applications. Imam An-Nawawi (may Allah have mercy on him), in his
        commentary on Sahih Muslim, explains that the distinction between
        pollinated fruits and unpollinated trees reflects the seller’s efforts,
        which must be honored unless explicitly transferred to the buyer. His
        commentary highlights how prophetic rulings were not arbitrary but built
        upon the moral logic of justice and clarity.
        <br />
        In conclusion, Islamic jurisprudence on land ownership and its produce
        is deeply rooted in principles of fairness, clarity, and justice. By
        addressing immovable assets, planted trees, naturally occurring
        resources, and crops, these rulings provide a comprehensive framework
        for transactions. Buyers and sellers are encouraged to engage in
        transparent dealings, ensuring that rights are preserved and disputes
        minimized. This system reflects the broader Islamic values of equity and
        communal welfare, as articulated in the Qur'an and Sunnah.
      </p>

      <p className="content">
        <b>Summary of Today's Lesson</b> <br />
        <br />
        Today we learned about the Islamic principles governing land ownership
        and the produce it yields. We discussed that when land is sold, all
        immovable structures such as walls, roofs, and doors are included by
        default in the transaction. Trees planted on the land also transfer to
        the buyer, while already pollinated fruits remain with the seller unless
        specified otherwise, as supported by authentic Hadith. We also examined
        the rulings on naturally growing herbs, grass, and pasture. Islam
        considers these to be communal resources unless they are cultivated by
        human effort, in which case they become part of the property. Regarding
        crops, we learned that one-time crops like wheat remain with the seller
        until harvest, while recurring crops like clover are , the current yield
        goes to the seller, but future growth belongs to the buyer. These
        rulings reflect Islam’s emphasis on fairness, honoring prior effort, and
        preventing ambiguity in transactions. The legal framework ensures that
        both buyers and sellers are treated justly, resources are preserved, and
        community welfare is upheld in accordance with Islamic values.
        <br />
        <br />
        <b>Coming up next</b>
        <br />
        <br />
        In our next session, we will delve into the concept of As-Salam, a
        unique form of forward sale in Islamic commercial law. We will explore
        how this contract allows buyers to pay in advance for goods to be
        delivered later, offering a Shariah-compliant solution for financing,
        particularly in agriculture and trade. Drawing from the Qur'an,
        authentic Hadith, and scholarly interpretations, we will examine the
        essential conditions that make As-Salam valid, its economic wisdom, and
        how it safeguards both parties from uncertainty and injustice. Stay
        tuned as we continue our journey through Islamic commercial ethics with
        this impactful and widely used contract.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicTradePage8;
